/**
 * 📊 MONITOR DE SUSTENTAÇÃO DE GANHOS - Dashboard Premium
 * Componente C-Level para visualização de ROI e sustentação de melhorias
 */

import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart, ReferenceLine
} from 'recharts';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  DollarSign, Clock, Users, Target, AlertCircle, Plus, Link2
} from 'lucide-react';
import { useProjectStore } from '../store/useProjectStore';
import GainsMonitorService from '../services/gainsMonitorService';
import { SnapshotForm } from './SnapshotForm';
import { useTheme } from '../context/ThemeContext';
import type {
  GainsDashboardData,
  KaizenRecord,
  TrendChartData,
  RegressionAlert
} from '../types/gainsMonitor';

// ============================================================================
// CORES DO TEMA
// ============================================================================
const readThemeColor = (name: string, fallback: string) => {
  if (typeof window === 'undefined') return fallback;
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
export const GainsMonitorDashboard: React.FC<{
  organizationId: string;
  projectId?: string;
}> = ({ organizationId, projectId }) => {
  const { theme } = useTheme();
  const [showSnapshotForm, setShowSnapshotForm] = useState(false);
  const [snapshotKaizen, setSnapshotKaizen] = useState<KaizenRecord | null>(null);
  
  const [dashboardData, setDashboardData] = useState<GainsDashboardData>({
    total_kaizens: 0,
    active_kaizens: 0,
    sustained_kaizens: 0,
    regressed_kaizens: 0,
    total_lead_time_reduction: 0,
    total_cost_savings: 0,
    total_fte_freed: 0,
    avg_sustainability_rate: 0,
    avg_regression_time: 0,
    active_alerts: [],
    critical_alerts_count: 0
  });
  const [kaizens, setKaizens] = useState<KaizenRecord[]>([]);
  const [selectedKaizen, setSelectedKaizen] = useState<KaizenRecord | null>(null);
  const [trendData, setTrendData] = useState<TrendChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const colors = React.useMemo(() => ({
    success: readThemeColor('--risk-ok', '#12B76A'),
    warning: readThemeColor('--risk-alert', '#F79009'),
    danger: readThemeColor('--risk-critical', '#F04438'),
    primary: readThemeColor('--brand-primary', '#175CD3'),
    secondary: readThemeColor('--text-secondary', '#475467'),
    borderStrong: readThemeColor('--border-strong', '#D0D5DD'),
    borderSoft: readThemeColor('--border-soft', '#E4E7EC'),
    surface1: readThemeColor('--bg-surface-1', '#FFFFFF'),
    textPrimary: readThemeColor('--text-primary', '#101828'),
    textSecondary: readThemeColor('--text-secondary', '#475467'),
    textMuted: readThemeColor('--text-muted', '#98A2B3'),
  }), [theme]);

  const STATUS_COLORS: Record<string, string> = React.useMemo(() => ({
    sustentado: colors.success,
    em_monitoramento: colors.primary,
    regredido: colors.danger,
    implementado: colors.warning,
  }), [colors]);

  // Carregar dados
  useEffect(() => {
    loadData();
  }, [organizationId, projectId]);

  const loadData = async () => {
    setLoading(true);
    
    const [dashboard, kaizenList] = await Promise.all([
      GainsMonitorService.getDashboardData(organizationId),
      // 🔒 FIX: Filtrar kaizens por organização e projeto para isolamento multi-cliente
      GainsMonitorService.getKaizens({ organization_id: organizationId, project_id: projectId })
    ]);
    


    setDashboardData(dashboard);
    setKaizens(kaizenList);
    
    // Selecionar primeiro Kaizen automaticamente
    if (kaizenList.length > 0 && !selectedKaizen) {
      selectKaizen(kaizenList[0]);
    }

    setLoading(false);
  };

  const selectKaizen = async (kaizen: KaizenRecord) => {
    setSelectedKaizen(kaizen);
    const trend = await GainsMonitorService.getTrendData(kaizen.id);
    setTrendData(trend);
  };

  if (loading) {
    return (
      <div className="enterprise-page h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[var(--brand-primary)]"></div>
          <p className="enterprise-text-secondary">Carregando Monitor de Ganhos...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="enterprise-page text-center">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 enterprise-text-muted" />
        <p className="text-gray-600 dark:text-slate-400">Nenhum dado disponível</p>
      </div>
    );
  }

  return (
    <div className="enterprise-page h-full overflow-hidden">
      {/* KPI CARDS - Compacto */}
      <div className="enterprise-page-grid">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          
          {/* Total Economizado */}
          <MetricCard
            icon={<DollarSign className="w-6 h-6" />}
            title="Economias Acumuladas"
            value={GainsMonitorService.formatCurrency(dashboardData.total_cost_savings)}
            subtitle="Por ano"
            color="green"
            trend={+12.5}
          />

          {/* Lead Time Reduzido */}
          <MetricCard
            icon={<Clock className="w-6 h-6" />}
            title="Lead Time Reduzido"
            value={GainsMonitorService.formatTime(dashboardData.total_lead_time_reduction)}
            subtitle="Total economizado"
            color="blue"
            trend={+8.3}
          />

          {/* FTE Liberado */}
          <MetricCard
            icon={<Users className="w-6 h-6" />}
            title="Capacidade Liberada"
            value={`${dashboardData.total_fte_freed.toFixed(1)} FTE`}
            subtitle="Recursos disponíveis"
            color="purple"
          />

          {/* Alertas Críticos */}
          <MetricCard
            icon={<AlertTriangle className="w-6 h-6" />}
            title="Alertas Críticos"
            value={dashboardData.critical_alerts_count.toString()}
            subtitle={`${dashboardData.regressed_kaizens} Kaizens regredidos`}
            color={dashboardData.critical_alerts_count > 0 ? 'red' : 'gray'}
          />
        </div>

        {/* MAIN CONTENT */}
        <div className="mt-1 grid grid-cols-1 gap-3 lg:grid-cols-3" style={{ height: 'calc(100vh - 244px)' }}>
          
          {/* LEFT: Kaizen List */}
          <div className="lg:col-span-1 overflow-hidden">
            <KaizenListPanel
              kaizens={kaizens}
              selectedKaizen={selectedKaizen}
              onSelectKaizen={selectKaizen}
            />
          </div>

          {/* RIGHT: Trend Charts */}
          <div className="lg:col-span-2 space-y-3 overflow-y-auto">
            {selectedKaizen && trendData ? (
              <>
                {/* Gráfico de Tendência */}
                <TrendChart
                  kaizen={selectedKaizen}
                  trendData={trendData}
                  onAddSnapshot={(k) => {
                    setSnapshotKaizen(k);
                    setShowSnapshotForm(true);
                  }}
                />

                {/* Grid com Detalhes + Status lado a lado */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Detalhes do Kaizen */}
                  <KaizenDetailsPanel 
                    kaizen={selectedKaizen} 
                    onAddSnapshot={(k) => {
                      setSnapshotKaizen(k);
                      setShowSnapshotForm(true);
                    }}
                  />

                  {/* Status Comparison */}
                  <StatusComparisonChart kaizens={kaizens} />
                </div>
              </>
            ) : (
              <div className="enterprise-card-surface flex h-full items-center justify-center enterprise-text-muted">
                <div className="text-center">
                  <Target className="w-16 h-16 mx-auto mb-3 opacity-50" />
                  <p>Selecione um Kaizen para ver detalhes</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal: SnapshotForm */}
      {showSnapshotForm && snapshotKaizen && (
        <SnapshotForm
          kaizen={snapshotKaizen}
          onClose={() => {
            setShowSnapshotForm(false);
            setSnapshotKaizen(null);
          }}
          onSuccess={() => {
            loadData();
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// SUB-COMPONENTES
// ============================================================================

/**
 * Card de Métrica
 */
const MetricCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: string;
  subtitle: string;
  color: 'green' | 'blue' | 'purple' | 'red' | 'gray';
  trend?: number;
}> = ({ icon, title, value, subtitle, color, trend }) => {
  
  const colorClasses = {
    green: 'bg-green-50 dark:bg-green-500/15 text-green-600 dark:text-green-300 border-green-200 dark:border-green-800/40',
    blue: 'bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-300 border-blue-200 dark:border-blue-800/40',
    purple: 'bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-300 border-purple-200 dark:border-purple-800/40',
    red: 'bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-300 border-red-200 dark:border-red-800/40',
    gray: 'bg-gray-50 dark:bg-slate-500/15 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-700'
  };

  return (
    <div className="enterprise-card-surface p-4 transition-colors hover:bg-enterprise-surface-3">
      <div className={`inline-flex p-2 rounded ${colorClasses[color]} mb-2`}>
        {icon}
      </div>
      
      <div className="mb-1 text-xl font-bold enterprise-text-primary">
        {value}
      </div>
      
      <div className="mb-1 text-xs font-medium enterprise-text-secondary">
        {title}
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-xs enterprise-text-muted">{subtitle}</span>
        
        {trend !== undefined && (
          <span className={`flex items-center text-xs font-medium ${
            trend > 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {trend > 0 ? (
              <TrendingUp className="w-3 h-3 mr-1" />
            ) : (
              <TrendingDown className="w-3 h-3 mr-1" />
            )}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Lista de Kaizens
 */
const KaizenListPanel: React.FC<{
  kaizens: KaizenRecord[];
  selectedKaizen: KaizenRecord | null;
  onSelectKaizen: (kaizen: KaizenRecord) => void;
}> = ({ kaizens, selectedKaizen, onSelectKaizen }) => {
  
  return (
    <div className="enterprise-card-surface flex h-full flex-col p-4">
      <h2 className="mb-3 flex items-center gap-2 text-base font-bold enterprise-text-primary">
        <Target className="h-4 w-4 text-[var(--brand-primary)]" />
        Kaizens Ativos ({kaizens.length})
      </h2>
      
      <div className="space-y-2 overflow-y-auto flex-1">
        {kaizens.map(kaizen => {
          const isSelected = selectedKaizen?.id === kaizen.id;
          const gainPercentage = GainsMonitorService.calculateGainPercentage(
            kaizen.baseline_lead_time,
            kaizen.target_lead_time,
            kaizen.current_lead_time || kaizen.target_lead_time
          );

          return (
            <button
              key={kaizen.id}
              onClick={() => onSelectKaizen(kaizen)}
              className={`w-full text-left p-3 rounded border-2 transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/15 dark:border-blue-400'
                  : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 bg-white dark:bg-slate-900'
              }`}
            >
              <div className="flex items-start justify-between mb-1">
                <span className="font-semibold text-gray-900 dark:text-slate-100 text-sm line-clamp-1">
                  {kaizen.title}
                </span>
                
                <span
                  className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap ml-2"
                  style={{
                    backgroundColor: `${STATUS_COLORS[kaizen.status]}20`,
                    color: STATUS_COLORS[kaizen.status]
                  }}
                >
                  {kaizen.status}
                </span>
              </div>
              
              <div className="flex items-center justify-between text-xs text-gray-600 dark:text-slate-400">
                <span>{kaizen.responsible}</span>
                <span className={`font-medium ${
                  gainPercentage >= 100 ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'
                }`}>
                  {gainPercentage.toFixed(0)}% do target
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Gráfico de Tendência de Lead Time
 */
const TrendChart: React.FC<{
  kaizen: KaizenRecord;
  trendData: TrendChartData;
  onAddSnapshot: (kaizen: KaizenRecord) => void;
}> = ({ kaizen, trendData, onAddSnapshot }) => {
  
  const chartData = [
    {
      date: 'Baseline',
      value: trendData.baseline,
      target: trendData.target,
      isBaseline: true,
      dateSort: 0
    },
    ...trendData.snapshots.map((s, i) => ({
      date: new Date(s.date).toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: 'short',
        year: '2-digit'
      }),
      value: s.value,
      target: trendData.target,
      isTargetMet: s.is_target_met,
      dateSort: new Date(s.date).getTime()
    }))
  ].sort((a, b) => a.dateSort - b.dateSort);

  const hasMultipleSnapshots = trendData.snapshots.length >= 2;
  const hasSingleSnapshot = trendData.snapshots.length === 1;

  return (
    <div className="bg-white dark:bg-slate-900 rounded shadow-sm border border-gray-200 dark:border-slate-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-bold text-gray-900 dark:text-slate-100">{trendData.kaizen_title}</h2>
          <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
            Tendência de Lead Time • Target: {GainsMonitorService.formatTime(trendData.target)}
          </p>
        </div>
        
        {kaizen.status === 'sustentado' && (
          <div className="flex items-center gap-2 px-3 py-1 bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300 rounded">
            <CheckCircle2 className="w-4 h-4" />
            <span className="font-medium text-sm">Sustentado</span>
          </div>
        )}
        
        {kaizen.status === 'regredido' && (
          <div className="flex items-center gap-2 px-3 py-1 bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300 rounded">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-medium text-sm">Regressão Detectada</span>
          </div>
        )}
      </div>

      {/* Aviso quando há poucos dados */}
      {hasSingleSnapshot && (
        <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-800/40 rounded flex items-start gap-2">
          <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-300 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">📊 Primeiro registro inserido!</p>
            <p>Continue adicionando medições mensais para visualizar a evolução da sustentação.</p>
          </div>
        </div>
      )}

      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
            </linearGradient>
          </defs>
          
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.15)" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 11, fill: 'var(--text-secondary, #64748b)' }}
            stroke="rgba(148, 163, 184, 0.35)"
            angle={-15}
            textAnchor="end"
            height={60}
          />
          <YAxis 
            tick={{ fontSize: 12, fill: 'var(--text-secondary, #64748b)' }}
            stroke="rgba(148, 163, 184, 0.35)"
            label={{ 
              value: 'Lead Time (min)', 
              angle: -90, 
              position: 'insideLeft',
              style: { fontSize: 12, fill: 'var(--text-secondary, #64748b)' }
            }}
          />
          
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--bg-surface, white)',
              border: '1px solid var(--border-default, #e5e7eb)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--text-primary, #0f172a)',
            }}
            formatter={(value: number) => [
              GainsMonitorService.formatTime(value),
              'Lead Time'
            ]}
            labelFormatter={(label) => `Data: ${label}`}
          />
          
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          
          {/* Linha do Target */}
          <ReferenceLine
            y={trendData.target}
            stroke={COLORS.success}
            strokeDasharray="5 5"
            strokeWidth={2}
            label={{ 
              value: 'Meta', 
              position: 'right', 
              fill: COLORS.success,
              fontSize: 11
            }}
          />
          
          {/* Área de valores */}
          <Area
            type="monotone"
            dataKey="value"
            stroke={COLORS.primary}
            strokeWidth={hasMultipleSnapshots ? 3 : 0}
            fill="url(#colorValue)"
            name="Lead Time Atual"
            dot={{ 
              r: 5, 
              fill: COLORS.primary, 
              strokeWidth: 2, 
              stroke: 'var(--bg-surface, #fff)' 
            }}
            activeDot={{ r: 7 }}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Legenda informativa */}
      {trendData.snapshots.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between text-xs text-gray-600 dark:text-slate-400">
          <div className="flex items-center gap-4">
            <span>
              <strong>{trendData.snapshots.length}</strong> {trendData.snapshots.length === 1 ? 'medição' : 'medições'} registradas
            </span>
            {hasMultipleSnapshots && (
              <span className="text-green-600 dark:text-green-400 font-medium">
                ✓ Evolução sendo monitorada
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => onAddSnapshot(kaizen)}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
            title="Adicionar nova medição do mês atual"
          >
            + Adicionar medição do mês
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Painel de Detalhes do Kaizen
 */
const KaizenDetailsPanel: React.FC<{
  kaizen: KaizenRecord;
  onAddSnapshot: (kaizen: KaizenRecord) => void;
}> = ({ kaizen, onAddSnapshot }) => {
  const getActions = useProjectStore(s => s.getActions);
  const linkedActions = (kaizen.linked_action_ids || []).map(id => getActions().find(a => a.id === id)).filter(Boolean);

  const reduction = ((kaizen.baseline_lead_time - (kaizen.current_lead_time || kaizen.target_lead_time)) / kaizen.baseline_lead_time) * 100;

  return (
    <div className="bg-white dark:bg-slate-900 rounded shadow-sm border border-gray-200 dark:border-slate-700 p-3 h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100">Métricas de Performance</h3>
        <button
          onClick={() => onAddSnapshot(kaizen)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded shadow-sm hover:shadow-md transition-all duration-200"
        >
          <Plus className="w-4 h-4" />
          Medição
        </button>
      </div>
      
      <div className="grid grid-cols-3 gap-2 flex-1">
        {/* Baseline */}
        <div className="text-center">
          <div className="text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">BASELINE</div>
          <div className="text-lg font-bold text-gray-900 dark:text-slate-100">
            {GainsMonitorService.formatTime(kaizen.baseline_lead_time)}
          </div>
          <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Original</div>
        </div>
        
        {/* Target */}
        <div className="text-center">
          <div className="text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">TARGET</div>
          <div className="text-lg font-bold text-blue-600 dark:text-blue-300">
            {GainsMonitorService.formatTime(kaizen.target_lead_time)}
          </div>
          <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">Meta</div>
        </div>
        
        {/* Atual */}
        <div className="text-center">
          <div className="text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">ATUAL</div>
          <div className="text-lg font-bold text-green-600 dark:text-green-300">
            {GainsMonitorService.formatTime(kaizen.current_lead_time || kaizen.target_lead_time)}
          </div>
          <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
            <span className="font-medium text-green-600 dark:text-green-400">↓ {reduction.toFixed(1)}%</span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="font-medium text-gray-700 dark:text-slate-300">Progresso</span>
          <span className="font-bold text-blue-600 dark:text-blue-300">{reduction.toFixed(0)}%</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-slate-800 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(reduction, 100)}%` }}
          />
        </div>
      </div>

      {/* Metadata Compacto */}
      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-slate-700">
        <div>
          <div className="text-xs font-medium text-gray-600 dark:text-slate-400">Responsável</div>
          <div className="text-xs font-medium text-gray-900 dark:text-slate-100 truncate">{kaizen.responsible}</div>
        </div>
        <div>
          <div className="text-xs font-medium text-gray-600 dark:text-slate-400">Tipo</div>
          <div className="text-xs font-medium text-gray-900 dark:text-slate-100 capitalize truncate">
            {kaizen.type.replace(/_/g, ' ')}
          </div>
        </div>
      </div>

      {/* Ações Vinculadas */}
      {linkedActions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-1 mb-2">
            <Link2 className="w-3 h-3 text-blue-500 dark:text-blue-400" />
            <span className="text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wide">Ações Vinculadas</span>
            <span className="ml-auto text-xs text-gray-400 dark:text-slate-500">{linkedActions.length}</span>
          </div>
          <div className="flex flex-col gap-1">
            {linkedActions.map(a => (
              <div key={a!.id} className="flex items-center gap-1.5 text-xs bg-blue-50 dark:bg-blue-500/15 rounded px-2 py-1">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  a!.status === 'done' ? 'bg-green-500' :
                  a!.status === 'doing' ? 'bg-blue-500' : 'bg-gray-400 dark:bg-slate-500'
                }`} />
                <span className="truncate text-gray-700 dark:text-slate-300">{a!.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Gráfico de Status Comparison
 */
const StatusComparisonChart: React.FC<{
  kaizens: KaizenRecord[];
}> = ({ kaizens }) => {
  
  const statusData = Object.entries(
    kaizens.reduce((acc, k) => {
      acc[k.status] = (acc[k.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  ).map(([status, count]) => ({
    name: status.replace(/_/g, ' '),
    value: count,
    color: STATUS_COLORS[status] || COLORS.gray
  }));

  return (
    <div className="bg-white dark:bg-slate-900 rounded shadow-sm border border-gray-200 dark:border-slate-700 p-3 h-full flex flex-col">
      <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 mb-2">Distribuição por Status</h3>
      
      <div className="flex-1 flex items-center justify-center min-h-[160px]">
        <ResponsiveContainer width="100%" height={160}>
          <PieChart>
            <Pie
              data={statusData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={(entry) => `${entry.value}`}
              outerRadius={55}
              fill="#8884d8"
              dataKey="value"
            >
            {statusData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * Painel de Alertas
 */
const AlertsPanel: React.FC<{
  alerts: RegressionAlert[];
}> = ({ alerts }) => {
  
  return (
    <div className="bg-white dark:bg-slate-900 rounded shadow-sm border border-red-200 dark:border-red-800/40 p-3">
      <h3 className="text-sm font-bold text-red-900 dark:text-red-300 mb-2 flex items-center gap-1.5">
        <AlertTriangle className="w-4 h-4" />
        Alertas Críticos ({alerts.length})
      </h3>
      
      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {alerts.map(alert => (
          <div
            key={alert.id}
            className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800/40 rounded"
          >
            <div className="flex items-start justify-between mb-2">
              <span className="font-medium text-red-900 dark:text-red-200 text-sm">
                {alert.metric_name.replace('_', ' ').toUpperCase()}
              </span>
              <span className="text-xs text-red-700 dark:text-red-300 font-medium">
                +{alert.deviation_percentage.toFixed(1)}%
              </span>
            </div>
            
            <div className="text-xs text-red-700 dark:text-red-300">
              Target: {alert.target_value} → Atual: {alert.current_value}
            </div>
            
            <div className="text-xs text-gray-600 dark:text-slate-400 mt-2">
              Detectado em {new Date(alert.detected_at).toLocaleDateString('pt-BR')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GainsMonitorDashboard;
